import {useCallback, useEffect, useRef, useState} from 'react';
import {Skeleton, view, Icon, Action} from '@sail/ui';
import {clipboard} from '@sail/icons/react/Icon';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useSelector, setValue} from 'src/v2/state/store';
import {QrCode} from 'src/v2/components/common/QrCode';
import {Banner} from 'src/v2/components/common/Banner';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {InactivityOverlay} from 'src/v2/components/anyCrypto/InactivityOverlay';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {getNetworkName, getNetworkIcon} from 'src/components/Wallet/constants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {COLORS} from 'src/v2/util/color';
import {CountdownRing} from 'src/v2/components/common/CountdownRing';
import {useQuoteRefresh} from 'src/v2/components/anyCrypto/useQuoteRefresh';
import {useInactivityTimeout} from 'src/v2/components/anyCrypto/useInactivityTimeout';
import type {
  OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum,
  OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
} from '@stripe-internal/data-v1/schema/types';

const messages = defineMessages({
  youSend: {
    id: 'crypto.payins.v2.any_crypto.deposit.you_send',
    description: 'Label above the deposit amount',
    defaultMessage: 'You send',
  },
  amountOnNetwork: {
    id: 'crypto.payins.v2.any_crypto.deposit.amount_on_network',
    description: 'Amount and network for the deposit',
    defaultMessage: '{amount} {token} on {network}',
  },
  warning: {
    id: 'crypto.payins.v2.any_crypto.deposit.warning',
    description: 'Warning about sending correct asset',
    defaultMessage:
      'Only send <b>{token}</b> on <b>{network}</b>. Other assets may be lost.',
  },
  refreshCountdown: {
    id: 'crypto.payins.v2.any_crypto.deposit.refresh_countdown',
    description: 'Countdown timer for quote refresh',
    defaultMessage: 'Refresh in {seconds}s',
  },
  addressLabel: {
    id: 'crypto.payins.v2.any_crypto.deposit.address_label',
    description: 'Label for the deposit address',
    defaultMessage: 'Address',
  },
  copyAddress: {
    id: 'crypto.payins.v2.any_crypto.deposit.copy_address',
    description: 'Button to copy the deposit address',
    defaultMessage: 'Copy address',
  },
  copied: {
    id: 'crypto.payins.v2.any_crypto.deposit.copied',
    description: 'Button text after address is copied',
    defaultMessage: 'Copied!',
  },
  transactionDetection: {
    id: 'crypto.payins.v2.any_crypto.deposit.transaction_detection',
    description: 'Estimated time for transaction detection on slow networks',
    defaultMessage:
      'Transaction detection: ~{minutes} {minutes, plural, one {min} other {mins}}',
  },
  checkPaymentStatus: {
    id: 'crypto.payins.v2.any_crypto.deposit.check_payment_status',
    description: 'Link to check payment processing status',
    defaultMessage: 'Check payment status',
  },
});

type RequestRelayQuoteFn = (
  inputCurrency: OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum,
  inputChain: OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum,
  refundAddress?: string | null,
) => Promise<unknown>;

export const AnyCryptoDepositScreen = ({
  startPolling,
  requestRelayQuote,
}: {
  startPolling: () => void;
  requestRelayQuote: RequestRelayQuoteFn;
}) => {
  const intl = useIntl();
  const {isInactive, resume} = useInactivityTimeout();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedNetwork = useSelector(
    (state) => state.anyCryptoDeposit.selectedNetwork,
  );
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken!,
  );
  const depositAddress = useSelector(
    (state) => state.anyCryptoDeposit.quote.depositAddress,
  );
  const quoteAmount = useSelector(
    (state) => state.anyCryptoDeposit.quote.inputAmount,
  );
  const handleCopy = useCallback(() => {
    if (depositAddress) {
      navigator.clipboard.writeText(depositAddress);
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
      setIsCopied(true);
      copyTimerRef.current = setTimeout(() => setIsCopied(false), 2000);
    }
  }, [depositAddress]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  const {secondsUntilRefresh} = useQuoteRefresh(
    requestRelayQuote,
    selectedToken,
    selectedNetwork,
    isInactive,
  );

  useEffect(() => {
    startPolling();
  }, [startPolling]);

  const token = formatAssetCode(selectedToken);
  const networkName = selectedNetwork ? getNetworkName(selectedNetwork) : '';
  const currencyIcon = getCurrencyIcon(selectedToken);
  const isSlowNetwork =
    selectedNetwork === 'bitcoin' || selectedNetwork === 'ethereum';
  const detectionMinutes = selectedNetwork === 'bitcoin' ? 2 : 1;
  const networkIcon = selectedNetwork ? getNetworkIcon(selectedNetwork) : null;

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <view.div css={{stack: 'x', gap: 'space.150', alignY: 'center'}}>
        <Icon
          icon={currencyIcon}
          css={{
            width: 'space.500',
            height: 'space.500',
            flexShrink: 0,
            borderRadius: '50%',
          }}
        />
        <view.div css={{stack: 'y'}}>
          <Text type="body" css={{color: COLORS.textSubduedOnLight}}>
            {intl.formatMessage(messages.youSend)}
          </Text>
          <Text type="bodyEmphasized">
            {quoteAmount ? (
              intl.formatMessage(messages.amountOnNetwork, {
                amount: quoteAmount,
                token,
                network: networkName,
              })
            ) : (
              <Skeleton css={{width: '150px', height: 'medium'}} />
            )}
          </Text>
        </view.div>
      </view.div>

      <Banner
        message={
          <FormattedMessage
            {...messages.warning}
            values={{
              token,
              network: networkName,
              b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        }
        variant="warning"
      />

      <view.div
        css={{
          position: 'relative',
          borderRadius: 'small',
          borderColor: COLORS.borderOnLight,
          borderWidth: 'small',
          borderStyle: 'solid',
          overflow: 'hidden',
        }}
      >
        {isInactive ? <InactivityOverlay onResume={resume} /> : null}
        <view.div
          css={{
            stack: 'y',
            paddingTop: 'large',
            paddingBottom: 'medium',
            paddingX: 'space.150',
            gap: 'space.150',
            ...(isInactive ? {filter: 'blur(6px)', pointerEvents: 'none'} : {}),
          }}
        >
          <view.div css={{alignSelfX: 'center'}}>
            {depositAddress ? (
              <view.div css={{stack: 'z'}}>
                <QrCode qrCodeUri={depositAddress} excavateSize={60} />
                {networkIcon ? (
                  <view.div
                    css={{
                      alignSelfX: 'center',
                      alignSelfY: 'center',
                      backgroundColor: 'white',
                      borderRadius: 'xsmall',
                      padding: 'space.175',
                    }}
                  >
                    <Icon
                      icon={networkIcon}
                      css={{
                        width: 'xlarge',
                        height: 'xlarge',
                        borderRadius: 'xsmall',
                      }}
                    />
                  </view.div>
                ) : null}
              </view.div>
            ) : (
              <Skeleton css={{width: '266px', height: '266px'}} />
            )}
          </view.div>

          <view.div
            css={{
              stack: 'x',
              gap: 'small',
              alignY: 'center',
              justifyContent: 'center',
            }}
          >
            <CountdownRing
              seconds={secondsUntilRefresh}
              total={60}
              size={10}
              strokeWidth={1.5}
            />
            <Text type="caption" css={{color: COLORS.textSubduedOnLight}}>
              {intl.formatMessage(messages.refreshCountdown, {
                seconds: secondsUntilRefresh,
              })}
            </Text>
          </view.div>

          {depositAddress ? (
            <view.div css={{stack: 'y', gap: 'xsmall'}}>
              <Text type="caption" css={{color: COLORS.textSubduedOnLight}}>
                {intl.formatMessage(messages.addressLabel)}
              </Text>
              <Text
                type="body"
                css={{wordBreak: 'break-all', color: COLORS.textPrimary}}
              >
                {depositAddress}
              </Text>
            </view.div>
          ) : (
            <Skeleton css={{width: 'fill', height: 'xlarge'}} />
          )}

          {depositAddress ? (
            <BrandedButton onClick={handleCopy}>
              <view.div css={{stack: 'x', gap: 'small', alignY: 'center'}}>
                {isCopied ? null : (
                  <Icon
                    icon={clipboard}
                    css={{width: 'medium', height: 'medium'}}
                  />
                )}
                {intl.formatMessage(
                  isCopied ? messages.copied : messages.copyAddress,
                )}
              </view.div>
            </BrandedButton>
          ) : null}

          {isSlowNetwork ? (
            <view.div css={{stack: 'y', alignItems: 'center'}}>
              <Text
                type="caption"
                css={{
                  color: COLORS.textPrimary,
                  textAlign: 'center',
                  display: 'block',
                  width: 'fill',
                }}
              >
                {intl.formatMessage(messages.transactionDetection, {
                  minutes: detectionMinutes,
                })}
              </Text>
              <Action
                onPress={() =>
                  setValue((state) => {
                    state.anyCryptoDeposit.step = 'processing';
                  })
                }
              >
                <Text
                  type="captionEmphasized"
                  css={{
                    color: COLORS.textPrimary,
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'dotted',
                    cursor: 'pointer',
                  }}
                >
                  {intl.formatMessage(messages.checkPaymentStatus)}
                </Text>
              </Action>
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    </view.div>
  );
};
